import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, FileSpreadsheet, AlertTriangle, Check, Download, Loader2 } from 'lucide-react';

const BYTES_PER_KB = 1024;

export default function ImportPage() {
  const { api } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/import/schedule', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(data);
    } catch (e) {
      setResult({ errors: [e.response?.data?.detail || 'Upload failed'], shifts_created: 0, warnings: [] });
    }
    setUploading(false);
  };

  const downloadTemplate = () => {
    const headers = 'Employee,Date,Time In,Time Out,Service Line,Location,Notes\n';
    const example = 'Alyssa,2026-04-14,08:00,16:00,Explore Program,TNS 102,Riley; Dane; Seamus\n';
    const blob = new Blob([headers + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schedule_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Import Schedule
        </h1>
        <p className="text-base text-[#5A4D6E] mt-1">Upload Excel or CSV files to bulk-create shifts</p>
      </div>

      {/* Template Download */}
      <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="template-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#EDE7F6] rounded-lg flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-[#6B3FA0]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Download Template</h3>
              <p className="text-sm text-[#5A4D6E] mt-1">
                Use this template to format your schedule data. Required columns: <strong>Employee/Staff</strong>, <strong>Date</strong>, <strong>Time In/Start</strong>, <strong>Time Out/End</strong>.
                Optional: Service Line, Location, Notes (put participant initials here).
              </p>
              <Button variant="outline" onClick={downloadTemplate} className="mt-3 border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-2" data-testid="download-template-btn">
                <Download className="h-4 w-4" /> Download CSV Template
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="upload-card">
        <CardContent className="p-6">
          <div
            className="border-2 border-dashed border-[#DED8EB] rounded-lg p-8 text-center hover:border-[#9B7ED8] transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileRef} type="file" className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              data-testid="file-input"
            />
            <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-[#9B7ED8]" />
            <p className="text-[#2D1B4E] font-medium">
              {file ? file.name : 'Click to select a file'}
            </p>
            <p className="text-sm text-[#8E85A0] mt-1">
              Supports .xlsx, .xls, and .csv files
            </p>
          </div>

          {file && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-[#6B3FA0]" />
                <span className="text-sm text-[#2D1B4E] font-medium">{file.name}</span>
                <span className="text-xs text-[#8E85A0]">({(file.size / BYTES_PER_KB).toFixed(1)} KB)</span>
              </div>
              <Button onClick={handleUpload} disabled={uploading} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? 'Importing...' : 'Import Schedule'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="import-results">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-semibold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge className="bg-[#E8F5E9] text-[#2E7D32] border border-green-200 gap-1">
                <Check className="h-3 w-3" /> {result.shifts_created} shifts created
              </Badge>
            </div>

            {result.warnings?.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#F4A261] flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Warnings ({result.warnings.length})
                </p>
                <div className="bg-[#FFF3E0] border border-[#FFB74D] rounded-md p-3 max-h-40 overflow-y-auto">
                  {result.warnings.map((w, i) => (
                    <p key={`warn-${i}-${w.slice(0, 20)}`} className="text-xs text-[#E65100]">{w}</p>
                  ))}
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#D9534F]">Errors ({result.errors.length})</p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <p key={`err-${i}-${e.slice(0, 20)}`} className="text-xs text-[#D9534F]">{e}</p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="border-[#DED8EB] shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-[#2D1B4E] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Formatting Guide
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#5A4D6E]">
            <div>
              <p className="font-medium text-[#2D1B4E] mb-1">Required Columns:</p>
              <ul className="space-y-1 list-disc pl-4">
                <li><strong>Employee</strong> or <strong>Staff</strong> - Must match a name in the system</li>
                <li><strong>Date</strong> - Formats: YYYY-MM-DD, MM/DD/YYYY, M/D/YY</li>
                <li><strong>Time In</strong> or <strong>Time Start</strong> - HH:MM or H:MM AM/PM</li>
                <li><strong>Time Out</strong> or <strong>Time End</strong> - HH:MM or H:MM AM/PM</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-[#2D1B4E] mb-1">Optional Columns:</p>
              <ul className="space-y-1 list-disc pl-4">
                <li><strong>Service Line</strong> - Explore Program, In-Home, Employment</li>
                <li><strong>Location</strong> - Where the shift takes place</li>
                <li><strong>Notes</strong> - Participant initials, etc.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
